import { useMemo } from "react";
import intl from "react-intl-universal";
import {
  BAAS_SCOPE_PRESET_ISSUER,
  BAAS_SCOPE_PRESETS,
  groupScopesByPrefix,
  isScopePresetActive,
  resolvePresetScopes,
  toggleScopePreset,
} from "../baasInstanceCreateConstants";

const scopeGroupLabel = (groupKey) => {
  const i18nKey = `baas.instances.createPage.scopeGroup.${groupKey}`;
  const translated = intl.get(i18nKey);
  if (translated && translated !== i18nKey) return translated;
  return String(groupKey || "other").replace(/^./, (ch) => ch.toUpperCase());
};

/**
 * Grouped scope checkboxes + role presets (Emissor / Correntista).
 * Used by instance create and instance settings.
 */
export const BaasInstanceScopesPicker = ({
  catalog = [],
  selected = [],
  onChange,
  disabled = false,
  onPresetEnabled,
}) => {
  const scopeGroups = useMemo(() => groupScopesByPrefix(catalog), [catalog]);

  const toggleScope = (scope) => {
    if (disabled) return;
    onChange(
      selected.includes(scope)
        ? selected.filter((item) => item !== scope)
        : [...selected, scope],
    );
  };

  const selectAllScopes = () => {
    if (disabled) return;
    onChange([...(catalog || [])]);
  };

  const clearScopes = () => {
    if (disabled) return;
    onChange([]);
  };

  const selectGroupScopes = (groupScopes) => {
    if (disabled) return;
    const next = new Set(selected);
    for (const scope of groupScopes || []) next.add(scope);
    onChange(Array.from(next));
  };

  const clearGroupScopes = (groupScopes) => {
    if (disabled) return;
    const remove = new Set(groupScopes || []);
    onChange(selected.filter((scope) => !remove.has(scope)));
  };

  const handlePresetClick = (preset) => {
    if (disabled) return;
    const resolved = resolvePresetScopes(preset.scopes, catalog);
    if (resolved.length === 0) return;
    const wasActive = isScopePresetActive(selected, catalog, preset.scopes);
    onChange(toggleScopePreset(selected, catalog, preset));
    if (!wasActive) onPresetEnabled?.(preset);
  };

  const issuerActive = isScopePresetActive(
    selected,
    catalog,
    BAAS_SCOPE_PRESETS.find((item) => item.id === BAAS_SCOPE_PRESET_ISSUER)?.scopes,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.instances.createPage.scopePresetsHint")}
          </p>
          <div className="flex flex-wrap gap-2">
            {BAAS_SCOPE_PRESETS.map((preset) => {
              const resolved = resolvePresetScopes(preset.scopes, catalog);
              const active = isScopePresetActive(selected, catalog, preset.scopes);
              const includedInIssuer =
                preset.id !== BAAS_SCOPE_PRESET_ISSUER && issuerActive && active;
              const hintKey = `baas.instances.createPage.scopePreset.${preset.id}Hint`;
              const unavailable = resolved.length === 0;
              const title = unavailable
                ? intl.get("baas.instances.createPage.scopePresetUnavailable")
                : includedInIssuer
                  ? intl.get("baas.instances.createPage.scopePresetIncludedInIssuer")
                  : intl.get(hintKey);
              return (
                <button
                  key={preset.id}
                  type="button"
                  title={title}
                  aria-pressed={active}
                  disabled={disabled || unavailable}
                  onClick={() => handlePresetClick(preset)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                    active
                      ? "border-[#e7a73f] bg-[#e7a73f]/15 text-brand-dark dark:text-gray-100"
                      : "border-gray-200 text-gray-600 dark:border-brand-dark-border dark:text-gray-400"
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  {intl.get(`baas.instances.createPage.scopePreset.${preset.id}`)}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-outline !px-2.5 !py-1 !text-xs"
            onClick={selectAllScopes}
            disabled={disabled}
          >
            {intl.get("baas.instances.createPage.scopesSelectAll")}
          </button>
          <button
            type="button"
            className="btn-outline !px-2.5 !py-1 !text-xs"
            onClick={clearScopes}
            disabled={disabled}
          >
            {intl.get("baas.instances.createPage.scopesClear")}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {scopeGroups.map((group) => {
          const selectedInGroup = group.scopes.filter((scope) =>
            selected.includes(scope),
          ).length;
          return (
            <section
              key={group.key}
              className="rounded-xl border border-gray-200 p-3 dark:border-brand-dark-border sm:p-4"
            >
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                    {scopeGroupLabel(group.key)}
                  </h3>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {intl.get("baas.instances.createPage.scopeGroupSelected", {
                      selected: selectedInGroup,
                      total: group.scopes.length,
                    })}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn-outline !px-2 !py-0.5 !text-[11px]"
                    onClick={() => selectGroupScopes(group.scopes)}
                    disabled={disabled}
                  >
                    {intl.get("baas.instances.createPage.scopesSelectAll")}
                  </button>
                  <button
                    type="button"
                    className="btn-outline !px-2 !py-0.5 !text-[11px]"
                    onClick={() => clearGroupScopes(group.scopes)}
                    disabled={disabled}
                  >
                    {intl.get("baas.instances.createPage.scopesClear")}
                  </button>
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {group.scopes.map((scope) => {
                  const checked = selected.includes(scope);
                  return (
                    <label
                      key={scope}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                        checked
                          ? "border-[#e7a73f] bg-[#e7a73f]/10 text-brand-dark dark:text-gray-100"
                          : "border-gray-200 text-gray-600 dark:border-brand-dark-border dark:text-gray-400"
                      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#e7a73f]"
                        checked={checked}
                        onChange={() => toggleScope(scope)}
                        disabled={disabled}
                      />
                      <span className="font-mono text-xs">{scope}</span>
                    </label>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        {intl.get("baas.instances.createPage.scopesSelected", {
          count: selected.length,
        })}
      </p>
    </div>
  );
};
